import { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

/**
 * Figure note (#2139, epic #2138; DESIGN_SYSTEM.md 8.20): the words behind a
 * calculated figure, shown on hover and keyboard focus of the whole
 * label-and-value pair, never of the amount alone, and on a phone on tap.
 * A tapped note stays open until the pair is tapped again, anything else is
 * tapped, or the page scrolls. The note never carries a dollar amount.
 *
 * `enterDelay` (#2179) holds the hover back that many milliseconds, for
 * figures whose note used to sit under them in text; focus and tap stay
 * immediate.
 *
 * With no `note`, the children render plain.
 *
 * @param {object} props
 * @param {string} [props.note]        the equation in words
 * @param {string} [props.placement]   MUI Tooltip placement
 * @param {number} [props.enterDelay]  ms before a hover opens the note (default 0)
 * @param {React.ReactNode} props.children
 * @param {object} [props.sx]          extra sx merged onto the wrapping span
 */
const FigureNote = ({ note, placement = 'bottom-start', enterDelay = 0, children, sx, ...rest }) => {
  const [open, setOpen] = useState(false);
  // A tap opened it: hover and blur then leave it alone until it closes.
  const tapped = useRef(false);
  const pairRef = useRef(null);
  const hoverTimer = useRef(null);
  const clearHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  useEffect(() => clearHover, []);

  useEffect(() => {
    if (!open || !tapped.current) return undefined;
    const close = (e) => {
      if (e.type === 'pointerdown' && pairRef.current?.contains(e.target)) return;
      tapped.current = false;
      setOpen(false);
    };
    document.addEventListener('pointerdown', close, true);
    window.addEventListener('scroll', close, { capture: true, passive: true });
    return () => {
      document.removeEventListener('pointerdown', close, true);
      window.removeEventListener('scroll', close, { capture: true });
    };
  }, [open]);

  if (!note) return <Box component="span" sx={sx} {...rest}>{children}</Box>;

  return (
    <Tooltip
      title={note}
      open={open}
      placement={placement}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      slotProps={{
        tooltip: {
          sx: (theme) => ({
            fontSize: { xs: '13px', md: `${theme.ds.font.dim}px` },
            lineHeight: 1.4,
            maxWidth: 300,
          }),
        },
      }}
    >
      <Box
        ref={pairRef}
        component="span"
        tabIndex={0}
        data-figure-note
        onMouseEnter={() => {
          if (tapped.current) return;
          if (!enterDelay) { setOpen(true); return; }
          clearHover();
          hoverTimer.current = setTimeout(() => { if (!tapped.current) setOpen(true); }, enterDelay);
        }}
        onMouseLeave={() => { clearHover(); if (!tapped.current) setOpen(false); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!tapped.current) setOpen(false); }}
        onPointerDown={(e) => {
          if (e.pointerType !== 'touch') return;
          clearHover();
          tapped.current = !open;
          setOpen(!open);
        }}
        sx={{
          display: 'inline-flex',
          minWidth: 0,
          cursor: 'default',
          '&:focus-visible': (theme) => ({ outline: `1.5px solid ${theme.palette.interactive}`, outlineOffset: '2px' }),
          ...sx,
        }}
        {...rest}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export default FigureNote;
